/**
 * Filters user data to keep only essential information for GPT model processing
 * Uses a whitelist approach to minimize payload size
 */
export function extractCurrentCallUserData(userData: any): any {
    if (!userData) {
        return {};
    }
    // Deep clone to avoid mutating original
    const data = JSON.parse(JSON.stringify(userData));

    // Remove sensitive personal information at any level
    const sensitiveKeys = [
        'callId',
        'agentEmail',
        'clientEmail',
        'clientPhoneNumber',
        'crmLink',
        'first_name',
        'last_name',
        'email',
        'phone_number',
        'phone',
        'uuid',
        'userId',
        'id',
        'userIdData',
        'customer_action_type',
        'firstName',
        'lastName',
    ];

    // Remove large non-essential sections
    delete data.walkthroughState; // Entire UI state

    // Whitelist of keys to keep - focusing on business-relevant data for call assistance
    const keysToKeep = new Set([
        // Core quote and insurance data
        'quote',
        'quoteData',
        'temporary',
        'retirement',
        'iul',
        'term',
        'vul',
        'prosper',
        'product',
        'products', // Keep the small products array, not allProducts
        'carriers',
        'carrierName',
        'carrierCode',
        'productCode',
        'productTypeName',
        'productTypeCode',
        'name',
        'code',
        'modalPremium',
        'modal_premium',
        'faceAmount',
        'face_amount',
        'surrenderValue',
        'surrender_value',
        'cashValueAt65',
        'cashValueAt75',
        'cashValueAt85',
        'levelTermPeriod',
        'level_term_period',
        'termPeriod',
        'term_range',
        'termRange',
        'policyAge',
        'policy_age',
        'policyYear',
        'insuredAge',
        'riders',
        'riderRecommended',
        'riderAvailable',
        'hasCriticalRider',
        'hasChronicRider',
        'critical',
        'chronic',
        'productRank',
        'isPreferred',
        'productScope',
        'rider',

        // Essential user data only
        'gender',
        'genderId',
        'dateOfBirth',
        'date_of_birth',
        'age',
        'stateId',
        'state',
        'zip',
        'zipCode',
        'zipCodeState',
        'zipCodeId',

        // Health essentials
        'tobacco',
        'usedTobacco',
        'usedMarijuana',
        'weight',
        'weightPounds',
        'height',
        'heightInches',
        'feet',
        'inch',
        'disability',
        'hivAids',
        'aids',
        'asthma',
        'cancer',
        'diabetes',
        'heartAttack',
        'highBloodPressure',
        'highCholesterol',
        'sleepApnea',
        'stroke',
        'currentlyEmployed',
        'employmentStatus',

        // Financial essentials
        'annualHouseholdIncomeNumber',
        'annualHouseholdIncomeRange',
        'annualIndividualIncome',
        'annualHouseholdIncome',
        'household_income',
        'preferredMonthlySpend',
        'monthly_budget',
        'comfortableAmount',
        'mortgageAmount',
        'estimatedDebtTotal',
        'estimatedSavingTotal',
        'healthEmergencySavings',
        'growCashValue',

        // Goals and preferences
        'investmentGoals',
        'wealthGoals',
        'features',
        'intentGoals',
        'preferredInvestmentRisk',
        'investmentRisk',
        'investmentVehicles',
        'taxAdvantagedAccounts',

        // Family
        'maritalStatus',
        'numberOfChildren',
        'incomeDependents',

        // Recommendations
        'productTypeRecommendation',
        'recommendedProductType',
        'recommended_product_type',
        'recommendedProductTypeEnum',
        'calculatedQuote',
        'calculated_quote',
        'rating',
        'activeProduct',

        // Essential system data
        'hookEvent',
        'phase',
        'best',
        'basic',

        // Core containers (but we'll filter their contents)
        'userData',
        'walkthroughData',
        'calcStore',
    ]);

    // Function to recursively filter data
    function filterRecursive(obj: any): any {
        if (obj === null || typeof obj !== 'object') {
            return obj;
        }

        if (Array.isArray(obj)) {
            return obj.map((item) => filterRecursive(item));
        }

        const result: any = {};

        for (const [key, value] of Object.entries(obj)) {
            // Skip allProducts specifically - it's huge and redundant
            if (key === 'allProducts') {
                continue;
            }

            // Skip sensitive keys at any level
            if (sensitiveKeys.includes(key)) {
                continue;
            }

            if (keysToKeep.has(key)) {
                result[key] = filterRecursive(value);
            }
        }

        return result;
    }

    return filterRecursive(data);
}

/**
 * Extracts relevant data from parameterStore while removing PII and duplicates
 * Designed for providing context to AI chat completion
 */
export function extractParameterStoreData(parameterStore: any): any {
    if (!parameterStore) {
        return {};
    }

    // Deep clone to avoid mutating original
    const data = JSON.parse(JSON.stringify(parameterStore));

    // Remove or anonymize PII fields
    const piiFields = [
        'firstName',
        'lastName',
        'name',
        'contact',
        'agent',
        'emailAddress',
        'phoneNumber',
        'address',
        'addressCity',
        'addressZipCode',
        'socialSecurityNumber',
        'driversLicenseNumber',
        'familyContactPhoneNumber',
        'familyContactEmail',
        'familyContact',
        'policyOwnerName',
        'primaryCarePhysician',
        'specialistName',
        'employerAddress',
        'nameOfEmployer',
        'beneInfo1',
        'beneInfo2',
        'beneInfo3',
        'beneInfo4',
        'beneInfo5',
        'beneInfo6',
        'emergencyFirstName',
        'emergencyLastName',
        'emergencyEmail',
        'emergencyPhone',
        'emergencyStreetAddr',
        'applicationId',
        'instantIulApplicationId',
        'formid',
        'dealId',
    ];

    // Extract key business data sections
    const extracted: any = {
        // Basic demographics (anonymized)
        demographics: {
            age: data.age,
            gender: data.prosperRequest?.gender || data.gender,
            state: data.state || data.prosperRequest?.state,
            maritalStatus: data.approval?.maritalStatus?.value,
            height: data.height || data.approval?.height?.value,
            weight: data.weight || data.approval?.weight?.value,
        },

        // Product preferences
        productInfo: {
            formQuote: data.formQuote,
            productType: data.quote?.productType?.value,
            prosperSettings: data.prosperRequest
                ? {
                      prosperAmplifySolves: data.prosperRequest.prosperAmplifySolves,
                      prosperSpecify: data.prosperRequest.prosperSpecify,
                      prosperAmount: data.prosperRequest.prosperAmount,
                      prosperPremium: data.prosperRequest.prosperPremium,
                      prosperPremiumMode: data.prosperRequest.prosperPremiumMode,
                      prosperSmoker: data.prosperRequest.prosperSmoker,
                      prosperRiskClass: data.prosperRequest.prosperRiskClass,
                      prosperFaceAmount: data.prosperRequest.prosperFaceAmount,
                      prosperDeathBenefitOption: data.prosperRequest.prosperDeathBenefitOption,
                      prosperSolveGoal: data.prosperRequest.prosperSolveGoal,
                      prosperIndexName: data.prosperRequest.prosperIndexName,
                      prosperIndexedAllocationPercent: data.prosperRequest.prosperIndexedAllocationPercent,
                  }
                : null,
        },

        // Financial profile
        financial: {
            incomeIndividual: data.approval?.incomeIndividual?.value,
            monthlyBudget: data.prequal?.monthlyBudget?.value,
            estimatedSavingsTotal: data.approval?.estimatedSavingsTotal?.value,
            estimatedDebtTotal: data.approval?.estimatedDebtTotal?.value,
        },

        // Employment
        employment: {
            currentlyEmployed: data.approval?.currentlyEmployed?.value || data.prequal?.currentlyEmployed?.value,
            occupation: data.approval?.occupation?.value,
            timeEmployed: data.approval?.timeEmployed?.value,
            selfEmployed: data.approval?.currentSelfEmployed?.value,
        },

        // Health summary (anonymized)
        healthSummary: {
            tobacco: data.prequal?.tobacco?.value || data.TobaccoRating?.value,
            marijuana: data.MarijuanaRating?.value,
            generalHealthConditions: data.prequal?.healthConditions?.value,

            // Count of active conditions
            activeConditions: Object.entries(data.health || {})
                .filter(([key, val]: [string, any]) => val?.value === true)
                .map(([key]) => key),

            // Risk factors
            riskFactors: {
                dui: data.DUI?.value,
                criminalOffense: data.Criminaloffense?.value,
                bankruptcy: data.Bankruptcy?.value,
                extremeSports: data.extremeSports?.value,
                livingAssistance: data.livingAssistance?.value,
            },
        },

        // Insurance history
        insuranceHistory: {
            existingCoverage: data.existingCoverage?.value || data.prequal?.existingCoverage?.value,
            existingCarrier: data.existingCarrier?.value,
            existingCoverageAmount: data.existingCoverageAmount?.value,
            shoppedCoverage: data.shoppedCoverage?.value,
            policyEverBeenDeclined: data.policyEverBeenDeclined?.value,
        },

        // Family info (anonymized)
        family: {
            numberOfBeneficiaries: data.approval?.numberOfBenes?.value,
            hasDependents: data.prequal?.kids?.value,
            numberOfChildren: data.prequal?.kidsNumber?.value,
        },

        // Quote history summary
        quoteHistory: data.quoteHistory
            ? {
                  numberOfQuotes: data.quoteHistory.length,
                  latestQuote: data.quoteHistory[0]
                      ? {
                            productType: data.quoteHistory[0].singleQuote?.productType,
                            monthlyPremium: data.quoteHistory[0].singleQuote?.monthlyPremium,
                            faceAmount: data.quoteHistory[0].singleQuote?.faceAmount,
                        }
                      : null,
              }
            : null,

        // Recommended products
        recommendations: {
            productTypeRecommendation: data.recommendedProductsResponse?.productTypeRecommendation,
            qualifiedProductCodes: data.recommendedProductsResponse?.allEligibleProductCodes,
        },
    };

    // Remove null/undefined values and empty objects
    function cleanObject(obj: any): any {
        if (obj === null || obj === undefined) return undefined;
        if (typeof obj !== 'object') return obj;
        if (Array.isArray(obj)) {
            const cleaned = obj.map(cleanObject).filter((v) => v !== undefined);
            return cleaned.length > 0 ? cleaned : undefined;
        }

        const cleaned: any = {};
        for (const [key, value] of Object.entries(obj)) {
            const cleanedValue = cleanObject(value);
            if (cleanedValue !== undefined) {
                cleaned[key] = cleanedValue;
            }
        }

        return Object.keys(cleaned).length > 0 ? cleaned : undefined;
    }

    return cleanObject(extracted) || {};
}
